// How Humans Actually Learn Motor Skills. Cert masterclass (Unit 1).
// Mux: qOYS55cTCjTc3b02Iul00tWwVOLJiaQZUYo2pxW02IT8W8
//
// Authored from the talk's auto-caption transcript
// (knowledge-base/transcripts/cert/how-humans-learn-motor-skills.md).
// Quotes are lifted from what Coleman actually said. Bullets distill the
// practical takeaway. Coleman's voice, plain language, no em dashes.

export const MOTOR_LEARNING_MODULE_KEY = "how-humans-learn-motor-skills";

const VIDEO = "qOYS55cTCjTc3b02Iul00tWwVOLJiaQZUYo2pxW02IT8W8";

export const MOTOR_LEARNING_MODULE = {
  key: MOTOR_LEARNING_MODULE_KEY,
  title: "How Humans Actually Learn Motor Skills",
  sub: "What is really happening in the brain when a player builds a skill, and how to design practice that actually transfers to the game.",
  coverVideoId: VIDEO,
  duration: "Full talk plus a guided breakdown",
  chapters: [
    {
      id: "watch",
      title: "Watch the talk",
      blocks: [
        { kind: "text", md:
          "Watch it once start to finish, then come back through these sections for the ideas worth keeping. The order here is the order Coleman walks through on the talk: from what the brain is actually building, to the three systems doing the building, to how you design practice around it." },
        { kind: "video", muxPlaybackId: VIDEO,
          caption: "How Humans Actually Learn Motor Skills." },
      ],
    },

    {
      id: "retire-muscle-memory",
      title: "1. Retire the phrase \"muscle memory\"",
      blocks: [
        { kind: "pullquote", prominent: true, md:
          "Your muscle stores no memory. Zero. Muscle is just meat. It's what contracts when your nervous system tells it to. It has no idea what a jump shot is." },
        { kind: "text", md:
          "How we describe building skill shapes how we train it. \"Muscle memory\" sounds harmless, but it points you at the wrong place. The memory, the prediction, the learning, all of it lives in the brain and nervous system, not the muscle." },
        { kind: "text", md:
          "There is a grain of truth hiding inside the phrase, and you will get it back near the end. For now the point is simple: if you cannot describe learning past \"muscle memory,\" you are designing practice on folk theory. It is like being a doctor who has heard of germs but could not tell you what one is." },
      ],
    },

    {
      id: "prediction-machine",
      title: "2. The brain is building a prediction machine",
      blocks: [
        { kind: "text", md:
          "When a player learns a skill, here is what is actually changing in there: the brain is building a **prediction machine**. A skill is a really good prediction." },
        { kind: "text", md:
          "A jump shot, gather to release, takes under a second. Your body's feedback, the feel of your arm, the sight of the ball, takes about a tenth of a second just to travel up and get processed. So if you tried to shoot by reacting to feedback as it came in, the shot would already be gone." },
        { kind: "pullquote", md:
          "Shooting by reacting to feel is like steering a car by only looking in the rear view mirror. By the time you see you've drifted, you've already crashed." },
        { kind: "text", md:
          "So the brain does not control the shot by reacting. It controls it by predicting. Before you move, it runs a simulation: if I fire these commands to these muscles, the ball ends up here and it feels like this on the way. Then it fires, and reality either matches the prediction or it does not." },
        { kind: "text", md:
          "An expert shooter has a near perfect internal simulator. A beginner has a trash simulator, so their body is constantly surprised by what actually happens. **Learning a skill is making the prediction more accurate.**" },
      ],
    },

    {
      id: "error-is-the-engine",
      title: "3. Error is the engine of learning",
      blocks: [
        { kind: "text", md:
          "Your brain does not learn by storing the right movement. It learns by getting the prediction wrong, measuring how wrong it was, and then fixing the simulator." },
        { kind: "pullquote", prominent: true, md:
          "Error is the fuel of the entire machine. Being wrong on purpose, putting yourself in situations to be wrong, is how the simulator gets fixed." },
        { kind: "text", md:
          "Hold onto this, because it is the reason a clean, error-free practice can actually teach less than a messy one. No error, nothing to fix, no learning." },
      ],
    },

    {
      id: "perception-action",
      title: "4. A skill is perception glued to movement",
      blocks: [
        { kind: "text", md:
          "A skill is not just the movement. It is a movement glued to what you are perceiving, in one single loop. You read the defender's hips while you dribble, what you read changes your next move while it is happening, and that move changes what you perceive next. This is **perception action coupling.**" },
        { kind: "text", md:
          "Picture walking down a flight of stairs in daylight. Easy, automatic, you fly down without holding the rail. Now walk down those exact same stairs in pitch black. Suddenly your arms are out, you are feeling for each edge with your toe, creeping. Same stairs, same legs, same movement capacity." },
        { kind: "pullquote", md:
          "Why did the skill fall apart in the dark? You killed the perception. Take away the reading and there is barely a skill left." },
        { kind: "text", md:
          "A jump shot off a clean pass in an empty gym, and a jump shot with a hand flying at you while you rotate into it with less time to find the rim, look like the same skill on film. They are not. The perception feeding the movement is different, so the skill is different." },
        { kind: "assess", id: "perception-check", prompt:
          "Honestly, what share of your typical workout has real perception baked in (a live defender, a real read, a decision) versus movement in an empty gym?",
          lowLabel: "Almost all empty-gym", highLabel: "Almost all live reads" },
      ],
    },

    {
      id: "action-capacity",
      title: "5. The other half: action capacity",
      blocks: [
        { kind: "text", md:
          "There is a second half to this, and it matters. You still need legs strong enough to walk down the stairs in the first place. Perfect reading does you no good if your body physically cannot produce the movement the read is asking for." },
        { kind: "text", md:
          "That raw supply of movement, the strength, balance, footwork, the ability to get a shot off from a weird angle, is **action capacity.** And you absolutely can build it in isolation: strength and conditioning, a new footwork pattern, a new way to get a shot off in the air, even with no defender present." },
        { kind: "pullquote", md:
          "Movement vocabulary is the right word. The more words you have, the more sentences you can form. But the whole point is putting those words into a real sentence: a real defender, real time, responding to the game." },
        { kind: "callout", tone: "warn", title: "Where coaches go wrong", body:
          "Isolating a movement to build action capacity does not go against how we learn. The error is mistaking that isolated movement for the finished skill, and assuming it will plug straight into a game with all of the game's variables. The flashcards are not the conversation." },
      ],
    },

    {
      id: "three-systems",
      title: "6. Three learning systems running at once",
      blocks: [
        { kind: "text", md:
          "When you practice, you are not running one learning process. You are running at least three at the same time, in three parts of the brain, each learning from a different kind of error, each at a different speed. You cannot feed all three with the same drill, and most coaches accidentally train only one." },
        { kind: "principles", items: [
          { n: 1, title: "The Calibrator (cerebellum)", body:
            "Asks: did that feel like I predicted? It handles fine tuning. Miss left by an inch and it quietly nudges the simulator so the next prediction is sharper, like clicking a rifle scope. It is automatic and fast. The catch: it learns narrowly. It tunes the exact thing you practice under the exact conditions you practice it in." },
          { n: 2, title: "The Slot Machine (basal ganglia, dopamine)", body:
            "Asks: did that turn out better or worse than I expected? It cannot fix your mechanics, it only flags whether the whole attempt was worth keeping. And dopamine fires for surprise, not success. A tough contested make lights it up, a wide open make barely registers. It is slower, and it is the system that explores." },
          { n: 3, title: "The Dirt Path (raw repetition)", body:
            "Neurons that fire together wire together. Walk the same line across a field a thousand times and you wear a path your feet fall into automatically. Repetition carves a groove on its own, no perception or reward needed. It is the simplest system, and the most dangerous." },
        ]},
        { kind: "pullquote", prominent: true, md:
          "Repeat a broken jump shot thousands of times and you do not get a refined shot. You get a beautifully paved highway straight to a broken jump shot. You've made the flaw automatic." },
        { kind: "text", md:
          "There is the grain of truth inside \"muscle memory.\" The dirt path is real. But it is one of three systems, and it is the one that does not know right from wrong. A coach who only understands muscle memory is trusting the single system that has no idea whether the player is getting better, and ignoring the two that do." },
      ],
    },

    {
      id: "skill-relocates",
      title: "7. The skill moves through your brain (and what choking really is)",
      blocks: [
        { kind: "text", md:
          "As you get better, the skill physically relocates. Brain scans show it move. Learning to drive is the cleanest analogy." },
        { kind: "principles", items: [
          { n: 1, title: "Conscious (prefrontal cortex)", body:
            "Sixteen and learning to drive. Every movement is effortful and talked through. You could not hold a conversation if your life depended on it. This is you learning to shoot with your off hand: lift the elbow, snap the wrist, it feels strange so you think about it." },
          { n: 2, title: "Smoothing", body:
            "A few months in. Errors drop, it stops eating up so much attention, you can hold a conversation now. Not fully automatic, still a little shaky, but no longer a brain emergency." },
          { n: 3, title: "Automatic (deep motor centers)", body:
            "You drive home and realize you did not think about a single turn. The skill has moved out of the thinking brain entirely, which frees that brain for what actually wins games: reading the help, making the decision, or just hooping." },
        ]},
        { kind: "callout", tone: "warn", title: "What \"elbow in, follow through\" does mid-game", body:
          "Yell a mechanical cue at a player mid game and you drag an automatic, deep-brain skill back up into the slow conscious front of the brain, a stage they passed years ago. You turn the experienced driver back into the 16 year old stalling at a stoplight, and you made them worse for that rep." },
        { kind: "text", md:
          "That is what choking under pressure actually is. The moment feels huge, the player starts consciously monitoring a skill that is supposed to run automatically, the thinking brain reaches over and grabs the wheel, and the whole thing falls apart. How you cue matters enormously, because the goal is to keep them out of the thinking brain." },
      ],
    },

    {
      id: "wet-cement",
      title: "8. Most learning happens after practice",
      blocks: [
        { kind: "text", md:
          "A huge chunk of what a player learns does not happen during the workout. It happens afterward, in the hours after, and especially in their sleep." },
        { kind: "pullquote", md:
          "Practice is like leaving wet cement. During the session you press the shapes in, but the cement is still soft. It sets later, mostly overnight." },
        { kind: "text", md:
          "Players genuinely get better overnight with zero extra practice. The brain replays the day's reps during sleep and cleans them up, so a player can walk in the next day having seemingly learned something without touching a ball in between. The cement set." },
        { kind: "text", md:
          "This is also why learning is nonlinear. The cement may not set for weeks or months, then the skill spikes. So spacing matters, and an ugly session that did not click by the end can still be doing real work once the player goes home and sleeps." },
      ],
    },

    {
      id: "performance-vs-learning",
      title: "9. Performance is not learning (the big one)",
      blocks: [
        { kind: "text", md:
          "If you take one idea from this whole talk, make it this one. Confusing these two can completely kill a player's results." },
        { kind: "compare",
          leftTitle: "Performance",
          leftItems: [
            "How good they look right now, in the session",
            "Warm, grooved, rep 400, succeeding",
            "Feels great to the coach and the player",
            "The least trustworthy sign that learning happened",
          ],
          rightTitle: "Learning",
          rightItems: [
            "How good they are later, somewhere else, under pressure",
            "Transfer: does the skill adapt to new situations",
            "Retention: does it still hold next week and next season",
            "Often produced by sessions that looked worse",
          ],
        },
        { kind: "pullquote", prominent: true, md:
          "Looking good at rep 400 when you're warm and grooved tells you almost nothing about how much the athlete learned in a way that shows up next Tuesday in a game." },
        { kind: "text", md:
          "Something clicking mid session is not proof of learning. It might be improving, but you cannot be certain it will transfer to the game or even survive to the next practice. And a session that never clicked is not proof that nothing was learned. We have to be okay with ugly." },
      ],
    },

    {
      id: "block-vs-random",
      title: "10. Block vs random practice, and desirable difficulties",
      blocks: [
        { kind: "text", md:
          "Two ways to run a workout. Player A blocks it: 50 of the same shot, then 50 of the same move, one thing at a time, grooving each pattern. Player B is random: every rep is a little different, shot then move then pass, the brain never gets to settle on one thing for 50 straight reps." },
        { kind: "compare",
          leftTitle: "Player A (block) looks better",
          leftItems: [
            "Smooth, in rhythm, grooved, confident",
            "Succeeds faster, cleaner-looking session",
            "Solves the problem once, then recites it 49 times",
            "Brain coasts, the cement barely sets",
          ],
          rightTitle: "Player B (random) looks worse",
          rightItems: [
            "Choppy, resetting every rep, kind of ugly",
            "A week later, tested in a game-like setting, wins big",
            "Every rep is a new problem to rebuild from scratch",
            "Same demand a real game makes, so the cement sets harder",
          ],
        },
        { kind: "pullquote", md:
          "Your job as a coach is not to make practice look clean. It's to find the difficulties that are worth keeping around." },
        { kind: "text", md:
          "These are desirable difficulties: they feel bad in the moment and build durable skill. They make the practice look worse and make the player better long term. Here is the practical toolkit:" },
        { kind: "checklist", id: "desirable-difficulties", title: "Desirable difficulties to build in", items: [
          "Interleave skills instead of blocking (mix shots, moves, decisions)",
          "Vary the conditions: same skill, different spots, distances, passes, footwork",
          "Space it out: force water breaks, shorter more frequent sessions over one long grind",
          "Pull back the feedback: stop cueing every rep so they don't depend on your voice",
          "Add live perception: a real defender, a real read, real time",
        ]},
        { kind: "callout", tone: "tip", title: "When blocking is the right call", body:
          "A player still in the conscious thinking phase of a specific skill needs to block it first to solve the problem at all. That is true even for an advanced player who is a beginner at one particular footwork or read. Block until they solve it a couple times, then add variability. Block is a tool for building confidence and grooving a path, just not the primary method." },
      ],
    },

    {
      id: "gym-shooter",
      title: "11. Why the gym shooter can't shoot in the game",
      blocks: [
        { kind: "text", md:
          "Now the five o'clock hitter makes sense, the player who is phenomenal in the workout and cannot buy a bucket in the game. Remember the calibrator, the system that tunes your shot but tunes it narrowly." },
        { kind: "pullquote", md:
          "Your brain never learned shooting. It learned shooting in an empty gym, no defender, no clock, no pressure, off a perfect pass from the same spot. That whole bundle got baked into the skill." },
        { kind: "text", md:
          "Then a game shows up and the conditions change every possession: a hand in the face, a closeout at a weird angle, fatigue, a shot clock, a crowd, a pass that leaves you slightly off balance. None of that was in the practice bundle, so the brain is asked to use the skill outside the exact edges it was tuned for, and it falls apart. The fix is to put more of the game into the practice." },
        { kind: "multipick", id: "transfer-fixes", prompt:
          "Which of these will you actually add to your next few sessions to widen the bundle?",
          options: [
            "A live or shadow defender on shooting reps",
            "Varying the pass, angle, and balance into the shot",
            "A shot clock or score-pressure constraint",
            "Fatigue before the skill rep, not just fresh reps",
            "Interleaving shots with moves and decisions",
          ]},
      ],
    },

    {
      id: "putting-it-together",
      title: "12. Putting it together",
      blocks: [
        { kind: "text", md:
          "Learning a skill is your brain building a prediction machine, a simulator of what your body will do, and it sharpens that machine through three systems at once: the calibrator that fine tunes off sensory error, the slot machine that keeps winning solutions off reward, and the dirt path that wears a groove from raw repetition for better or worse." },
        { kind: "text", md:
          "As the skill matures it moves out of the slow thinking brain into the fast automatic brain, which is the whole point, because it frees the player to read the game. Most of the setting in happens after practice, overnight. And to maximize transfer and retention, practice should contain conditions from the real game, not just cones in an empty gym." },
        { kind: "pullquote", prominent: true, md:
          "The practice that looks most productive, clean block reps in a gorgeous empty gym at 80 percent, constant cueing, is often the one that produces the least transferable skill." },
        { kind: "callout", tone: "quote", title: "The North Star", body:
          "Understand the science but do not blindly adhere to it. Understand tradition but do not blindly adhere to it. We still use block practice and cones, just in a way that is informed by how the brain actually works, so every decision in your craft is a more educated one." },
        { kind: "recap", id: "recap", title: "Your reflections from this masterclass" },
      ],
    },
  ],
};
